/**
 * 模块: SearchPerformanceSection
 * 职责: 搜索查询绩效数据批量上传和分析展示 - 主组件
 * 限制: <280行
 */

import React, { useState } from 'react';
import dayjs from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import { useSearchPerformance } from '../../../hooks/useSearchPerformance';
import { useSearchPerformanceUpload } from './useSearchPerformanceUpload';
import { generateYearOverview, getAvailableYears } from './yearOverviewUtils';
import { YearOverviewPanel } from './YearOverviewPanel';
import { PeriodListPanel } from './PeriodListPanel';
import { AnalysisTabsSection } from './AnalysisTabsSection';

dayjs.extend(weekOfYear);

interface SearchPerformanceSectionProps {
  skuId: number;
}

export const SearchPerformanceSection: React.FC<SearchPerformanceSectionProps> = ({ skuId }) => {
  const {
    periods,
    periodsLoading,
    advantageData,
    marketOverview,
    opportunityData,
    keywordPivotData,
    loading,
    fetchAdvantageKeywords,
    fetchMarketOverview,
    fetchOpportunityKeywords,
    fetchKeywordPivot,
    uploadCSV,
    deletePeriod,
  } = useSearchPerformance(skuId);

  const {
    uploading,
    uploadProgress,
    fileInputRef,
    handleFileSelect,
    triggerFileSelect,
  } = useSearchPerformanceUpload();

  const [selectedYear, setSelectedYear] = useState<number>(dayjs().year());
  const [selectedPeriodId, setSelectedPeriodId] = useState<number | null>(null);

  // 自动选择第一个周期
  React.useEffect(() => {
    if (periods.length > 0 && !selectedPeriodId) {
      setSelectedPeriodId(periods[0].id);
    }
  }, [periods, selectedPeriodId]);

  // 生成周期概览
  const weekOverview = generateYearOverview(selectedYear, periods);
  const uploadedCount = weekOverview.filter(w => w.uploaded).length;
  const availableYears = getAvailableYears(periods);

  // 包装uploadCSV函数以适配Hook期望的类型
  const handleUploadCSV = async (file: File): Promise<void> => {
    await uploadCSV(file);
  };

  // Tab切换处理（懒加载）
  const handleTabChange = (key: string) => {
    if (key === 'sp-advantage' && !advantageData) {
      fetchAdvantageKeywords();
    } else if (key === 'sp-overview' && !marketOverview) {
      fetchMarketOverview();
    } else if (key === 'sp-opportunity' && !opportunityData) {
      fetchOpportunityKeywords();
    } else if (key === 'sp-pivot' && !keywordPivotData) {
      fetchKeywordPivot();
    }
  };

  return (
    <div>
      {/* 隐藏的文件输入 */}
      <input
        type="file"
        ref={fileInputRef}
        multiple
        accept=".csv"
        style={{ display: 'none' }}
        onChange={(e) => handleFileSelect(e, handleUploadCSV)}
      />

      {/* 周期概览面板 */}
      <YearOverviewPanel
        selectedYear={selectedYear}
        setSelectedYear={setSelectedYear}
        uploadedCount={uploadedCount}
        availableYears={availableYears}
        weekOverview={weekOverview}
        uploading={uploading}
        uploadProgress={uploadProgress}
        triggerFileSelect={triggerFileSelect}
        onDelete={deletePeriod}
      />

      {/* 周期列表（折叠） */}
      <PeriodListPanel periods={periods} periodsLoading={periodsLoading} />

      {/* 数据分析 */}
      {periods.length > 0 && (
        <AnalysisTabsSection
          periodsCount={periods.length}
          selectedPeriodId={selectedPeriodId}
          advantageData={advantageData}
          marketOverview={marketOverview}
          opportunityData={opportunityData}
          keywordPivotData={keywordPivotData}
          loading={loading}
          onTabChange={handleTabChange}
        />
      )}
    </div>
  );
};

export default SearchPerformanceSection;

